import { z } from 'zod';
import { isoDateSchema } from './common.js';

export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 256;

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN, `Password must be at least ${PASSWORD_MIN} characters.`)
  .max(PASSWORD_MAX, `Password must be at most ${PASSWORD_MAX} characters.`);

export const emailSchema = z.string().trim().max(254).pipe(z.email('Enter a valid email address.'));

export const usernameSchema = z
  .string()
  .trim()
  .min(3, 'Username must be at least 3 characters.')
  .max(32, 'Username must be at most 32 characters.')
  .regex(
    /^[a-z0-9][a-z0-9_.-]*$/i,
    'Use letters, numbers, dots, dashes and underscores; start with a letter or number.',
  );

/** The signed-in user, as they see themselves. */
export const currentUserSchema = z.object({
  uuid: z.string(),
  email: z.string(),
  username: z.string(),
  isAdmin: z.boolean(),
  emailVerified: z.boolean(),
  twoFactorEnabled: z.boolean(),
  createdAt: isoDateSchema,
});
export type CurrentUser = z.infer<typeof currentUserSchema>;

export const publicPanelSchema = z.object({
  name: z.string(),
  registrationEnabled: z.boolean(),
});

export const sessionStateSchema = z.discriminatedUnion('authenticated', [
  z.object({
    authenticated: z.literal(false),
    /** True when a password was accepted and a TOTP/recovery code is still required. */
    twoFactorPending: z.boolean(),
    panel: publicPanelSchema,
  }),
  z.object({
    authenticated: z.literal(true),
    user: currentUserSchema,
    /** Send as X-CSRF-Token on every state-changing request. */
    csrfToken: z.string(),
    impersonator: z.object({ uuid: z.string(), username: z.string() }).nullable(),
    /** Admin 2FA is required by panel settings and this admin has not enabled it. */
    twoFactorSetupRequired: z.boolean(),
    panel: publicPanelSchema,
  }),
]);
export type SessionState = z.infer<typeof sessionStateSchema>;

export const loginRequestSchema = z.object({
  /** Email address or username. */
  login: z.string().trim().min(1, 'Enter your email or username.').max(254),
  password: z.string().min(1, 'Enter your password.').max(PASSWORD_MAX),
});

export const loginResponseSchema = z.object({
  status: z.enum(['ok', 'two_factor_required']),
});

export const twoFactorChallengeSchema = z.object({
  /** A 6-digit TOTP code or a recovery code. */
  code: z.string().trim().min(6).max(32),
});

export const registerRequestSchema = z.object({
  email: emailSchema,
  username: usernameSchema,
  password: passwordSchema,
});

export const forgotPasswordSchema = z.object({ email: emailSchema });

export const resetPasswordSchema = z.object({
  token: z.string().min(20).max(128),
  password: passwordSchema,
});

export const verifyEmailSchema = z.object({ token: z.string().min(20).max(128) });
